import { axiosInstanceNoToast } from '../../../../fetchConfig/axiosConfig.js';
import { usePersistedDataStore } from '../../../store.js';
import logger from '../../../../utils/logger';
import { isTownViewDevNoPersist } from '../../../../utils/townViewDevMode.js';

/**
 * Generalized service to update Courtfiles in the backend with optional FE sync.
 * Mirrors the realtime offer update pattern (server-first, then local store update).
 *
 * @param {Object} params
 * @param {string} params.muniCode
 * @param {string|number} params.taxYear
 * @param {string[]} params.parcelIds - list of parcel_ids to update
 * @param {Object} params.fieldsToUpdate - fields for DB update (e.g., { scarDeterminationValue: 50000, caseInfo: 'Updated info' })
 * @param {boolean} params.updateFrontend - if true, reflect changes in negotiation object
 * @param {Object} [params.frontendOnlyCaseUpdates] - per-parcel fields merged into the negotiation object only (not sent to the API)
 * @param {boolean} [params.townView] - when true in development, skip DB persist (FE only)
 */
export const updateCourtfilesGeneralized = async (params) => {
  const {
    muniCode,
    taxYear,
    parcelIds,
    fieldsToUpdate,
    stipulation,
    updateFrontend = true,
    frontendOnlyCaseUpdates,
    townView,
    clearArt7FiledParcelIds,
  } = params || {};

  const skipDbPersist = isTownViewDevNoPersist(townView);

  if (!muniCode || !taxYear || !Array.isArray(parcelIds) || parcelIds.length === 0) {
    throw new Error('Missing required parameters for updateCourtfilesGeneralized');
  }

  if (!fieldsToUpdate || Object.keys(fieldsToUpdate).length === 0) {
    throw new Error('No fields to update provided for updateCourtfilesGeneralized');
  }

  // 1) Backend update (authoritative)
  let data;
  if (skipDbPersist) {
    logger.log('[TownView dev] Skipping update_courtfiles_generalized — FE store only');
    data = { success: true, message: 'Courtfiles updated (dev, FE only)', updated_count: parcelIds.length };
  } else {
    let response;
    try {
      response = await axiosInstanceNoToast.post('/update_courtfiles_generalized', {
        MuniCode: muniCode,
        TaxYear: taxYear,
        parcelIds: parcelIds,
        fieldsToUpdate,
        stipulation,
        clearArt7FiledParcelIds: Array.isArray(clearArt7FiledParcelIds) ? clearArt7FiledParcelIds : [],
      });
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        'Failed to update Courtfiles';
      throw new Error(msg);
    }

    data = response.data || {};
    if (!data.success) {
      throw new Error(data.error || 'Failed to update Courtfiles');
    }
    if (data.updated_count === 0) {
      throw new Error(
        data.error ||
          'No Courtfiles rows were updated. Check that parcel IDs and tax year match Courtfiles.'
      );
    }
  }

  // 2) Optionally mirror to FE store
  if (updateFrontend) {
    const getNegotiationObj = usePersistedDataStore.getState().negotiationObj;
    const setNegotiationObj = usePersistedDataStore.getState().setNegotiationObj;
    const setNegotiationObjVersion = usePersistedDataStore.getState().setNegotiationObjVersion;

    if (getNegotiationObj && setNegotiationObj && setNegotiationObjVersion) {
      // Create a deep copy of the negotiation object
      const updatedNegotiationObj = JSON.parse(JSON.stringify(getNegotiationObj));
      
      // Update each case with the new courtfiles data
      parcelIds.forEach(parcelId => {
        const caseIndex = updatedNegotiationObj.cases.findIndex(c => c.parcel_id === parcelId);
        if (caseIndex !== -1) {
          // Check if fieldsToUpdate is nested (per-parcel structure) or flat
          const caseUpdates = fieldsToUpdate[parcelId] || fieldsToUpdate;
          const feOnly = frontendOnlyCaseUpdates?.[parcelId];
          updatedNegotiationObj.cases[caseIndex] = {
            ...updatedNegotiationObj.cases[caseIndex],
            ...caseUpdates,
            ...(feOnly && typeof feOnly === 'object' ? feOnly : {}),
          };
        }
      });

      // Update the store
      setNegotiationObj(updatedNegotiationObj);
      setNegotiationObjVersion(prev => prev + 1);
      
      logger.log('✅ [COURTFILES UPDATE] Frontend updated successfully');
    }
  }

  return {
    success: true,
    message: data.message || 'Courtfiles updated successfully',
    parcelIds,
    fieldsToUpdate,
    updated_count: data.updated_count,
    lambda_responses: data.lambda_responses,
  };
};
